import { useState } from "react";
import Reveal from "@/components/home/Reveal";
import PlanCard from "@/components/home/PlanCard";
import PlanComparison from "@/components/home/PlanComparison";
import ExchangeRateBadge from "@/components/home/ExchangeRateBadge";
import { useRegisterModal } from "@/components/home/RegisterModalProvider";
import { waUrl, emailUrl, planWaText } from "@/lib/whatsapp";
import pricing from "@/content/pricing.json";
import { ChevronDown, Mail, ShieldCheck, Clock, RefreshCcw } from "lucide-react";

const GRACE_ICONS = [ShieldCheck, Clock, RefreshCcw];

export const Pricing = () => {
  const { openModal } = useRegisterModal();
  const [annual, setAnnual] = useState(false);
  const [showTable, setShowTable] = useState(false);

  return (
    <section id="precios" className="mt-section bg-brand-cream relative overflow-hidden scroll-mt-20" data-testid="pricing-section">
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
      <div className="mt-container relative">
        <Reveal className="max-w-3xl">
          <p className="text-brand-orange font-semibold text-sm uppercase tracking-wider">Planes y precios</p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl leading-tight">{pricing.headline}</h2>
          <p className="mt-4 text-base md:text-lg text-gray-600">{pricing.subheadline}</p>
        </Reveal>

        <Reveal delay={100} className="mt-8 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <ExchangeRateBadge />
          <div className="inline-flex items-center self-start lg:self-auto rounded-full bg-white border border-orange-200 p-1 shadow-sm" role="tablist" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-brand-carbon text-white" : "text-gray-600 hover:text-brand-carbon"}`} data-testid="billing-monthly">Mensual</button>
            <button onClick={() => setAnnual(true)} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${annual ? "bg-brand-carbon text-white" : "text-gray-600 hover:text-brand-carbon"}`} data-track="precio-toggle-anual" data-testid="billing-annual">
              Anual <span className="text-[11px] font-bold bg-brand-green text-white px-2 py-0.5 rounded-full">{pricing.annual_discount_label}</span>
            </button>
          </div>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-6 items-stretch">
          {pricing.plans.map((plan, i) => (
            <Reveal key={plan.id} delay={i * 90} className="flex">
              <PlanCard plan={plan} annual={annual} index={i} onSelect={(p) => openModal({ plan: p.name, source: "plan" })} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={120} className="mt-10 grid md:grid-cols-3 gap-4" data-testid="grace-section">
          {pricing.grace.items.map(({ title, text }, i) => {
            const Icon = GRACE_ICONS[i];
            return (
              <div key={title} className="rounded-2xl bg-white border border-orange-100 p-5 flex gap-4" data-testid={`grace-item-${i + 1}`}>
                <span className="w-10 h-10 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center shrink-0"><Icon className="w-5 h-5" /></span>
                <div><p className="font-semibold text-brand-carbon leading-snug">{title}</p><p className="mt-1 text-sm text-gray-600 leading-relaxed">{text}</p></div>
              </div>
            );
          })}
        </Reveal>

        <Reveal delay={80} className="mt-10 text-center">
          <button onClick={() => setShowTable(!showTable)} className="inline-flex items-center gap-2 font-semibold text-brand-orange-dark hover:text-brand-orange transition-colors" data-track="precio-comparar" data-testid="toggle-comparison-button" aria-expanded={showTable}>
            {showTable ? "Ocultar comparación completa" : "Ver comparación completa de los 4 planes"} <ChevronDown className={`w-5 h-5 transition-transform ${showTable ? "rotate-180" : ""}`} />
          </button>
          {showTable && <div className="mt-6 text-left"><PlanComparison /></div>}
        </Reveal>

        <Reveal delay={100} className="mt-12 rounded-3xl bg-brand-carbon text-white p-7 lg:p-9 flex flex-col lg:flex-row lg:items-center gap-6 justify-between" data-testid="pricing-contact-strip">
          <div>
            <h3 className="text-2xl">¿No sabes cuál te conviene?</h3>
            <p className="mt-2 text-gray-300 max-w-xl">Escríbele a Richard: te dice con honestidad si el plan Free te basta. Alta express en minutos con tu menú completo, tu logotipo y tu número exclusivo de WhatsApp Business.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a href={waUrl(planWaText("que me recomiendes"))} target="_blank" rel="noopener noreferrer" className="btn-mt btn-mt-wa" data-track="precio-cotizacion" data-testid="pricing-whatsapp-link"><i className="fab fa-whatsapp text-xl" /> Pedir recomendación</a>
            <a href={emailUrl()} className="btn-mt btn-mt-white" data-track="precio-email" data-testid="pricing-email-link"><Mail className="w-5 h-5" /> richard@menuthere.com</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
